import React, {useState, useEffect} from 'react';
import {
  Box,
  Typography,
  Button,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  Select,
  FormControl,
  IconButton,
  Tooltip,
  Collapse,
  TextField,
  MenuItem,
  Menu,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import EditIcon from '@mui/icons-material/Edit';
import {useParams, useNavigate} from 'react-router-dom';
import {useQuery, useMutation, useApolloClient} from '@apollo/client';
import {GET_EVAL_TEST_CASES, GET_EVAL_REPORTS, GET_EVAL_REPORT, GET_EVAL_PROMPTS, GET_JOB, GET_OPENROUTER_MODELS} from '../graphql/queries';
import {RUN_EVAL, DELETE_EVAL_TEST_CASE, DELETE_EVAL_REPORT, SAVE_EVAL_PROMPT, RENAME_EVAL_PROMPT, DELETE_EVAL_PROMPT, ITERATE_EVAL, RUN_MULTI_MODEL_EVAL, RECALCULATE_EVAL_REPORTS, RERUN_EVAL_CASE, RERUN_EVAL_REPORT} from '../graphql/mutations';
import {useToast} from '../contexts/ToastContext';
import EvalSummaryBar from '../components/eval/EvalSummaryBar';
import EvalResultsTable from '../components/eval/EvalResultsTable';
import EvalCompareView from '../components/eval/EvalCompareView';
import PromptModelGrid, {type GridCellJob} from '../components/eval/PromptModelGrid';
import PromptCostPanel from '../components/eval/PromptCostPanel';
import {formatPricePer1M} from '../utils/format';

const HOW_IT_WORKS_STEPS = [
  {
    title: '1. Flag test cases from the Source Library',
    body: 'Open any analyzed item in the Source Library drawer, click the overflow menu (\u22EE), and select "Flag as eval case". Adjust guideable, topic, and audience to what\'s actually correct, then save.',
  },
  {
    title: '2. Run the prompt against all cases',
    body: 'Click "Run Eval" above. This re-runs the analysis prompt against every test case and compares the result to your expected values.',
  },
  {
    title: '3. See what\'s wrong',
    body: 'The Results tab shows overall pass rate and per-field accuracy. Click any row to see exact diffs (e.g. "topic: expected \'Health & Safety\', got \'Other\'").',
  },
  {
    title: '4. Iterate on the prompt',
    body: 'Go to the Prompts tab and click "Iterate" to have multiple LLMs generate improved prompts, evaluate them, and pick the winner. Repeat for multiple iterations to converge on the best prompt.',
  },
];

function formatScore(p: any): string {
  if (!p.lastEvalScore) return '';
  const score = p.lastEvalScore.weightedScore ?? p.lastEvalScore.guideableAccuracy ?? p.lastEvalScore.accuracy;
  return ` (${score}%)`;
}

const TAB_NAMES = ['overview', 'prompts', 'results', 'test-cases'] as const;
const EVAL_BASE = '/org/eval';

export default function EvalPage() {
  const {showToast} = useToast();
  const navigate = useNavigate();
  const {tab: tabParam, reportId: reportParam} = useParams<{tab?: string; reportId?: string}>();

  // Derive active tab from URL path
  const activeTab = Math.max(0, TAB_NAMES.indexOf((tabParam || 'overview') as any));
  const setActiveTab = (index: number) => {
    const name = TAB_NAMES[index] || 'overview';
    navigate(name === 'overview' ? EVAL_BASE : `${EVAL_BASE}/${name}`);
  };

  // Derive selected report from URL path
  const [selectedReportIds, _setSelectedReportIds] = useState<string[]>(reportParam ? [reportParam] : []);
  const setSelectedReportIds = (ids: string[]) => {
    _setSelectedReportIds(ids);
    if (ids.length === 1) {
      navigate(`${EVAL_BASE}/results/${ids[0]}`);
    }
  };

  // Sync state when URL changes (browser back/forward)
  useEffect(() => {
    if (reportParam && !selectedReportIds.includes(reportParam)) {
      _setSelectedReportIds([reportParam]);
    } else if (!reportParam && selectedReportIds.length > 0) {
      _setSelectedReportIds([]);
    }
  }, [tabParam, reportParam]);
  const [howOpen, setHowOpen] = useState(false);

  // Prompt editor state
  const [editingPromptName, setEditingPromptName] = useState<string | null>(null);
  const [editorContent, setEditorContent] = useState('');
  const [newPromptName, setNewPromptName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Rename prompt state
  const [renamingPrompt, setRenamingPrompt] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // Run eval dialog
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [runDialogPrompt, setRunDialogPrompt] = useState<string>('current');
  const [runDialogModels, setRunDialogModels] = useState<Set<string>>(new Set(['meta-llama/llama-3.1-8b-instruct']));

  // Re-eval all prompts dialog
  const [reEvalAllOpen, setReEvalAllOpen] = useState(false);

  // Model selector
  const [evalModel, setEvalModel] = useState<string>('meta-llama/llama-3.1-8b-instruct');

  // Iterate dialog state
  const [iterateOpen, setIterateOpen] = useState(false);
  const [iterateGenerationModels, setIterateGenerationModels] = useState<Set<string>>(new Set(['anthropic/claude-sonnet-4', 'openai/gpt-5.4']));
  const [iterateIterations, setIterateIterations] = useState(2);

  // Report filter
  const [hideOrphanedReports, setHideOrphanedReports] = useState(true);

  // Background job tracking
  const [evalJobId, setEvalJobId] = useState<string | null>(null);
  const [evalJobType, setEvalJobType] = useState<string | null>(null);

  // Grid cell jobs — track running evals per (prompt, model) for progress indicators
  const [gridCellJobs, setGridCellJobs] = useState<GridCellJob[]>([]);

  // Cost panel — expanded prompt for cost breakdown
  const [costPanelPrompt, setCostPanelPrompt] = useState<string | null>(null);

  // Queries
  const {data: casesData, refetch: refetchCases} = useQuery(GET_EVAL_TEST_CASES);
  const {data: reportsData, refetch: refetchReports} = useQuery(GET_EVAL_REPORTS);
  const {data: promptsData, refetch: refetchPrompts} = useQuery(GET_EVAL_PROMPTS);

  const selectedReportId = selectedReportIds.length === 1 ? selectedReportIds[0] : null;

  const {data: reportData, loading: reportLoading} = useQuery(GET_EVAL_REPORT, {
    variables: {id: selectedReportId},
    skip: !selectedReportId,
  });

  const {data: baselineData} = useQuery(GET_EVAL_REPORT, {
    variables: {id: reportData?.evalReport?.baselineReportId},
    skip: !reportData?.evalReport?.baselineReportId,
  });

  // Compare mode: fetch both selected reports
  const {data: compareAData} = useQuery(GET_EVAL_REPORT, {
    variables: {id: selectedReportIds[0]},
    skip: selectedReportIds.length !== 2,
  });

  const {data: compareBData} = useQuery(GET_EVAL_REPORT, {
    variables: {id: selectedReportIds[1]},
    skip: selectedReportIds.length !== 2,
  });

  // Poll for job status using Firestore job query
  const {data: jobData} = useQuery(GET_JOB, {
    variables: {id: evalJobId},
    skip: !evalJobId,
    pollInterval: 3000,
  });

  // Handle job completion
  useEffect(() => {
    if (!evalJobId || !jobData?.job) return;
    const job = jobData.job;

    if (job.status === 'SUCCEEDED') {
      const result = job.resultData;
      if (evalJobType === 'EVAL_MULTI_MODEL' && result?.reports) {
        refetchReports();
        setActiveTab(0); // Grid view shows the new results
        showToast({message: `Multi-model eval complete: ${result.reports.length} models evaluated`, severity: 'success'});
        setEvalJobId(null);
        setEvalJobType(null);
        return;
      }
      if (evalJobType === 'EVAL_ITERATE' && result?.winnerPromptName) {
        refetchPrompts().then(({data}) => {
          const match = data?.evalPrompts?.find((p: any) => p.name === result.winnerPromptName);
          if (match) {
            setEditingPromptName(match.name);
            setEditorContent(match.prompt);
            setIsCreating(false);
            setActiveTab(1);
          }
        });
        if (result?.winnerReportId) {
          setSelectedReportIds([result.winnerReportId as string]);
        }
        showToast({message: `Iteration complete: best "${result.winnerDisplayName}" at ${result.winnerScore}%`, severity: 'success'});
      } else if (result?.reportId) {
        const isGridJob = gridCellJobs.some(j => j.jobId === evalJobId);
        if (isGridJob) {
          // Triggered from grid — stay on overview, just refresh
          showToast({message: `Eval complete: ${result.passed}/${result.total} passed`, severity: 'success'});
        } else {
          setSelectedReportIds([result.reportId as string]);
          setActiveTab(2);
          showToast({message: `Eval complete: ${result.passed}/${result.total} passed`, severity: 'success'});
        }
      }
      refetchReports();
      refetchPrompts();
      setGridCellJobs(prev => prev.filter(j => j.jobId !== evalJobId));
      setEvalJobId(null);
      setEvalJobType(null);
    } else if (job.status === 'FAILED') {
      setGridCellJobs(prev => prev.filter(j => j.jobId !== evalJobId));
      showToast({message: `Eval failed: ${job.errorMessage || 'Unknown error'}`, severity: 'error'});
      setEvalJobId(null);
      setEvalJobType(null);
    }
  }, [jobData, evalJobId, evalJobType]);

  // Poll grid cell jobs for completion (supports multiple concurrent jobs)
  const apolloClient = useApolloClient();
  useEffect(() => {
    if (gridCellJobs.length === 0) return;
    const interval = setInterval(async () => {
      const completed: string[] = [];
      for (const cellJob of gridCellJobs) {
        try {
          const {data} = await apolloClient.query({query: GET_JOB, variables: {id: cellJob.jobId}, fetchPolicy: 'network-only'});
          const job = data?.job;
          if (job?.status === 'SUCCEEDED' || job?.status === 'FAILED') {
            completed.push(cellJob.jobId);
            if (job.status === 'FAILED') {
              showToast({message: `Eval failed for ${cellJob.modelName.split('/').pop()}: ${job.errorMessage || 'Unknown error'}`, severity: 'error'});
            }
          }
        } catch { /* ignore polling errors */ }
      }
      if (completed.length > 0) {
        setGridCellJobs(prev => prev.filter(j => !completed.includes(j.jobId)));
        refetchReports();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [gridCellJobs, apolloClient, refetchReports, showToast]);

  // Mutations
  const [runEvalMutation] = useMutation(RUN_EVAL);
  const [iterateEvalMutation] = useMutation(ITERATE_EVAL);
  const [deleteTestCase] = useMutation(DELETE_EVAL_TEST_CASE);
  const [deleteReportMutation] = useMutation(DELETE_EVAL_REPORT);
  const [saveEvalPromptMutation] = useMutation(SAVE_EVAL_PROMPT);
  const [renameEvalPromptMutation] = useMutation(RENAME_EVAL_PROMPT);
  const [deleteEvalPromptMutation] = useMutation(DELETE_EVAL_PROMPT);
  const [runMultiModelEvalMutation] = useMutation(RUN_MULTI_MODEL_EVAL);
  const [recalculateMutation, {loading: recalculating}] = useMutation(RECALCULATE_EVAL_REPORTS);
  const [rerunCaseMutation] = useMutation(RERUN_EVAL_CASE);
  const [rerunReportMutation] = useMutation(RERUN_EVAL_REPORT);

  // Multi-model eval state
  const [multiModelSelected, setMultiModelSelected] = useState<Set<string>>(new Set(['meta-llama/llama-3.2-3b-instruct', 'mistralai/mistral-7b-instruct', 'meta-llama/llama-3.1-8b-instruct', 'mistralai/mistral-nemo']));
  const [multiModelPrompt, setMultiModelPrompt] = useState<string>('');
  const [multiModelCustom, setMultiModelCustom] = useState<string>('');

  // Multi-model table sort & filter
  const [mmSortCol, setMmSortCol] = useState<string>('score');
  const [mmSortAsc, setMmSortAsc] = useState<boolean>(false);
  const [mmFilterPrompt, setMmFilterPrompt] = useState<string>('');

  // OpenRouter pricing - fetch for selected + suggested models
  const SUGGESTED_MODELS = React.useMemo(() => [
    'meta-llama/llama-3.2-3b-instruct',
    'mistralai/mistral-7b-instruct',
    'meta-llama/llama-3.1-8b-instruct',
    'mistralai/mistral-nemo',
    'google/gemma-3n-e4b-it',
    'mistralai/mistral-small-3.1-24b-instruct',
    'mistralai/mistral-small-3.2-24b-instruct',
    'google/gemini-2.5-flash',
    'google/gemini-2.5-flash-lite',
    'meta-llama/llama-4-scout',
    'qwen/qwen3-8b',
    'deepseek/deepseek-chat-v3-0324',
    'anthropic/claude-haiku-4.5',
    'anthropic/claude-sonnet-4',
    'anthropic/claude-opus-4',
    'openai/gpt-4o-mini',
    'openai/gpt-oss-120b',
    'liquid/lfm2-8b-a1b',
  ], []);
  // Higher-capability models for prompt generation (meta-prompt engineering)
  const GENERATION_MODELS = React.useMemo(() => [
    'anthropic/claude-sonnet-4',
    'openai/gpt-5.4',
    'google/gemini-2.5-flash',
    'anthropic/claude-haiku-4.5',
    'deepseek/deepseek-chat-v3-0324',
    'anthropic/claude-opus-4',
    'openai/gpt-4o-mini',
    'meta-llama/llama-4-scout',
    'mistralai/mistral-small-3.2-24b-instruct',
  ], []);

  const pricingModelIds = React.useMemo(() => {
    const all = new Set(SUGGESTED_MODELS);
    multiModelSelected.forEach(m => all.add(m));
    GENERATION_MODELS.forEach(m => all.add(m));
    iterateGenerationModels.forEach(m => all.add(m));
    return Array.from(all);
  }, [SUGGESTED_MODELS, multiModelSelected, GENERATION_MODELS, iterateGenerationModels]);
  const {data: orModelsData} = useQuery(GET_OPENROUTER_MODELS, {
    variables: {ids: pricingModelIds},
    skip: activeTab !== 0 && activeTab !== 1 && !iterateOpen && !runDialogOpen && !reEvalAllOpen,
  });
  const orPricing: Record<string, {name: string; inputPer1M: number; outputPer1M: number}> = React.useMemo(() => {
    const map: Record<string, {name: string; inputPer1M: number; outputPer1M: number}> = {};
    for (const m of orModelsData?.openRouterModels || []) {
      map[m.id] = {name: m.name, inputPer1M: m.inputPer1M, outputPer1M: m.outputPer1M};
    }
    return map;
  }, [orModelsData]);

  const testCases = casesData?.evalTestCases || [];
  const reports = reportsData?.evalReports || [];
  const prompts = promptsData?.evalPrompts || [];
  const report = reportData?.evalReport;
  const baselineReport = baselineData?.evalReport;
  const reportAData = compareAData?.evalReport;
  const reportBData = compareBData?.evalReport;

  const evalJobActive = !!evalJobId;

  const handleRunEval = async (promptName?: string, modelName?: string) => {
    setRunDialogOpen(false);
    const usePrompt = promptName === 'current' ? undefined : promptName;
    const useModel = modelName || evalModel;
    try {
      const result = await runEvalMutation({variables: {promptName: usePrompt, modelName: useModel}});
      const job = result.data?.runEval;
      if (job) {
        setEvalJobId(job.id);
        setEvalJobType('EVAL_RUN');
        showToast({
          message: `Eval started${usePrompt ? ` with "${usePrompt}"` : ''} on ${useModel.split('/').pop()}...`,
          severity: 'info',
          action: {label: 'View job', to: `/org/jobs/${job.id}`},
        });
      }
    } catch (err: any) {
      showToast({message: `Eval failed: ${err.message}`, severity: 'error'});
    }
  };

  const handleReEvalAllPrompts = async () => {
    setReEvalAllOpen(false);
    const allPrompts = prompts.map((p: any) => p.name);
    let started = 0;
    for (const promptName of allPrompts) {
      try {
        const usePrompt = promptName === 'current' ? undefined : promptName;
        await runEvalMutation({variables: {promptName: usePrompt, modelName: evalModel}});
        started++;
      } catch (err: any) {
        console.error(`Failed to start eval for "${promptName}": ${err.message}`);
      }
    }
    showToast({message: `Started ${started} eval job(s) across all prompts`, severity: 'info'});
    refetchReports();
  };

  const handleIterate = async (promptName?: string) => {
    const genModels = Array.from(iterateGenerationModels);
    if (genModels.length === 0) {
      showToast({message: 'Select at least one generation model', severity: 'error'});
      return;
    }
    setIterateOpen(false);
    try {
      const result = await iterateEvalMutation({
        variables: {
          promptName: promptName || undefined,
          evalModelName: evalModel,
          generationModelNames: genModels,
          iterations: iterateIterations,
        },
      });
      const job = result.data?.iterateEval;
      if (job) {
        setEvalJobId(job.id);
        setEvalJobType('EVAL_ITERATE');
        showToast({
          message: `Iteration started: ${iterateIterations}x with ${genModels.length} model(s)`,
          severity: 'info',
          action: {label: 'View job', to: `/org/jobs/${job.id}`},
        });
      }
    } catch (err: any) {
      showToast({message: `Iterate failed: ${err.message}`, severity: 'error'});
    }
  };

  const handleDeleteReport = async (id: string) => {
    try {
      await deleteReportMutation({variables: {id}});
      setSelectedReportIds(selectedReportIds.filter(rid => rid !== id));
      refetchReports();
      showToast({message: 'Report deleted', severity: 'success'});
    } catch (err: any) {
      showToast({message: `Delete failed: ${err.message}`, severity: 'error'});
    }
  };

  const handleDeleteCase = async (id: string) => {
    try {
      await deleteTestCase({variables: {id}});
      refetchCases();
      showToast({message: 'Test case deleted', severity: 'success'});
    } catch (err: any) {
      showToast({message: `Delete failed: ${err.message}`, severity: 'error'});
    }
  };

  const handleEditPrompt = (prompt: any) => {
    setEditingPromptName(prompt.name);
    setEditorContent(prompt.prompt);
    setIsCreating(false);
  };

  const handleNewPrompt = () => {
    setEditingPromptName(null);
    setEditorContent('');
    setNewPromptName('');
    setIsCreating(true);
  };

  const handleSavePrompt = async () => {
    const name = isCreating ? newPromptName.trim() : editingPromptName;
    if (!name) {
      showToast({message: 'Prompt name is required', severity: 'error'});
      return;
    }
    if (name === 'current') {
      showToast({message: 'Cannot overwrite the built-in prompt', severity: 'error'});
      return;
    }
    try {
      await saveEvalPromptMutation({variables: {name, prompt: editorContent}});
      refetchPrompts();
      setEditingPromptName(name);
      setIsCreating(false);
      showToast({message: `Prompt "${name}" saved`, severity: 'success'});
    } catch (err: any) {
      showToast({message: `Save failed: ${err.message}`, severity: 'error'});
    }
  };

  const handleDeletePrompt = async (name: string) => {
    try {
      await deleteEvalPromptMutation({variables: {name}});
      refetchPrompts();
      if (editingPromptName === name) {
        setEditingPromptName(null);
        setEditorContent('');
      }
      showToast({message: `Prompt "${name}" deleted`, severity: 'success'});
    } catch (err: any) {
      showToast({message: `Delete failed: ${err.message}`, severity: 'error'});
    }
  };

  const handleRenamePrompt = async (oldName: string) => {
    const newName = renameValue.trim();
    if (!newName || newName === oldName) {
      setRenamingPrompt(null);
      return;
    }
    try {
      await renameEvalPromptMutation({variables: {oldName, newName}});
      refetchPrompts();
      if (editingPromptName === oldName) setEditingPromptName(newName);
      setRenamingPrompt(null);
      showToast({message: `Renamed to "${newName}"`, severity: 'success'});
    } catch (err: any) {
      showToast({message: `Rename failed: ${err.message}`, severity: 'error'});
    }
  };

  const handleRunMultiModelEval = async () => {
    const modelNames = Array.from(multiModelSelected);
    if (modelNames.length === 0) {
      showToast({message: 'Select at least one model', severity: 'error'});
      return;
    }
    try {
      const result = await runMultiModelEvalMutation({
        variables: {
          promptName: multiModelPrompt || undefined,
          modelNames,
        },
      });
      const job = result.data?.runMultiModelEval;
      if (job) {
        setEvalJobId(job.id);
        setEvalJobType('EVAL_MULTI_MODEL');
        showToast({
          message: `Multi-model eval started (${modelNames.length} models)...`,
          severity: 'info',
          action: {label: 'View job', to: `/org/jobs/${job.id}`},
        });
      }
    } catch (err: any) {
      showToast({message: `Multi-model eval failed: ${err.message}`, severity: 'error'});
    }
  };

  const handleRecalculate = async () => {
    try {
      const result = await recalculateMutation();
      const count = result.data?.recalculateEvalReports ?? 0;
      refetchReports();
      refetchPrompts();
      showToast({message: `Recalculated ${count} records`, severity: 'success'});
    } catch (err: any) {
      showToast({message: `Recalculate failed: ${err.message}`, severity: 'error'});
    }
  };

  const toggleMultiModel = (name: string) => {
    setMultiModelSelected(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const addCustomModel = () => {
    const trimmed = multiModelCustom.trim();
    if (trimmed && !multiModelSelected.has(trimmed)) {
      setMultiModelSelected(prev => new Set(prev).add(trimmed));
      setMultiModelCustom('');
    }
  };

  // Reports grouped by model for the multi-model results view
  const multiModelReports = reports.filter((r: any) => r.modelName);

  const savedPrompts = prompts.filter((p: any) => p.name !== 'current');

  return (
    <Box sx={{p: 3, maxWidth: 1200, mx: 'auto'}}>
      {/* Header */}
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3}}>
        <Typography sx={{fontSize: 22, fontWeight: 700, color: '#111827'}}>Prompt Eval</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={evalJobActive ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
          onClick={() => { setRunDialogPrompt('current'); setRunDialogOpen(true); }}
          disabled={evalJobActive || testCases.length === 0}
          sx={{textTransform: 'none', fontSize: 13}}
        >
          {evalJobActive && evalJobType === 'EVAL_RUN' ? 'Running...' : 'Run Eval'}
        </Button>
      </Box>

      {/* How it works */}
      <Box sx={{mb: 2}}>
        <Box
          onClick={() => setHowOpen(!howOpen)}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            cursor: 'pointer', userSelect: 'none',
            '&:hover': {opacity: 0.8},
          }}
        >
          <HelpOutlineIcon sx={{fontSize: 16, color: '#9CA3AF'}} />
          <Typography sx={{fontSize: 13, color: '#6B7280', fontWeight: 500}}>How it works</Typography>
          <KeyboardArrowDownIcon sx={{fontSize: 16, color: '#9CA3AF', transform: howOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s'}} />
        </Box>
        <Collapse in={howOpen}>
          <Box sx={{mt: 1.5, p: 2, bgcolor: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB'}}>
            <Typography sx={{fontSize: 13, color: '#4B5563', mb: 2, lineHeight: 1.6}}>
              Build a regression test suite for the source analysis prompt. Flag documents with their correct classification, then re-run the prompt to catch regressions when you change it.
            </Typography>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
              {HOW_IT_WORKS_STEPS.map((step) => (
                <Box key={step.title}>
                  <Typography sx={{fontSize: 13, fontWeight: 600, color: '#111827', mb: 0.25}}>{step.title}</Typography>
                  <Typography sx={{fontSize: 12, color: '#6B7280', lineHeight: 1.5}}>{step.body}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{mt: 2, p: 1.5, bgcolor: '#F9FAFB', borderRadius: '8px'}}>
              <Typography sx={{fontSize: 12, color: '#6B7280', lineHeight: 1.5}}>
                <strong>Asserts on 3 fields:</strong> guideable (bool), topic (19 categories), audience (External/Internal/Both).
                Test cases live in <code style={{fontSize: 11, background: '#F3F4F6', padding: '1px 4px', borderRadius: 3}}>eval/test-cases.json</code> (version controlled).
                Reports are saved locally and not committed.
              </Typography>
            </Box>
          </Box>
        </Collapse>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{mb: 2, '& .MuiTab-root': {textTransform: 'none', fontSize: 13, minHeight: 40}}}
      >
        <Tab label="Overview" />
        <Tab label="Prompts" icon={<EditIcon sx={{fontSize: 16}} />} iconPosition="start" />
        <Tab label="Results" />
        <Tab label={`Test Cases (${testCases.length})`} />
      </Tabs>

      {/* Tab 0: Prompt x Model Grid */}
      {activeTab === 0 && (<>
        <PromptModelGrid
          reports={reports}
          prompts={prompts}
          modelPricing={orPricing}
          onCellClick={(reportId) => {
            setSelectedReportIds([reportId]);
            setActiveTab(2);
          }}
          runningJobs={gridCellJobs}
          onJobClick={(jobId) => navigate(`/org/jobs/${jobId}`)}
          onRunEval={async (promptName, modelName) => {
            try {
              const usePrompt = promptName === 'current' ? undefined : promptName;
              const result = await runEvalMutation({variables: {promptName: usePrompt, modelName}});
              const job = result.data?.runEval;
              if (job) {
                setGridCellJobs(prev => [...prev, {promptName, modelName, jobId: job.id}]);
                // Don't set evalJobId — grid jobs are tracked via gridCellJobs polling
              }
            } catch (err: any) {
              showToast({message: `Failed to start eval: ${err.message}`, severity: 'error'});
            }
          }}
          onPromptCostClick={(name) => setCostPanelPrompt(costPanelPrompt === name ? null : name)}
        />

        {/* Cost breakdown panel */}
        {costPanelPrompt && (<Box sx={{mt: 2}}>

          <PromptCostPanel
            promptName={costPanelPrompt}
            promptDisplayName={prompts.find((p: any) => p.name === costPanelPrompt)?.displayName || costPanelPrompt}
            reports={reports}
            allModelIds={SUGGESTED_MODELS}
            modelPricing={orPricing}
            runningJobs={gridCellJobs}
            onClose={() => setCostPanelPrompt(null)}
            onReportClick={(reportId) => {
              setSelectedReportIds([reportId]);
              setActiveTab(2);
            }}
            onRunEval={async (promptName, modelName) => {
              try {
                const usePrompt = promptName === 'current' ? undefined : promptName;
                const result = await runEvalMutation({variables: {promptName: usePrompt, modelName}});
                const job = result.data?.runEval;
                if (job) {
                  setGridCellJobs(prev => [...prev, {promptName, modelName, jobId: job.id}]);
                  setEvalJobId(job.id);
                  setEvalJobType('EVAL_RUN');
                }
              } catch (err: any) {
                showToast({message: `Failed to start eval: ${err.message}`, severity: 'error'});
              }
            }}
            onJobClick={(jobId) => navigate(`/org/jobs/${jobId}`)}
          />
        </Box>)}
      </>)}

      {/* Tab 2: Results */}
      {activeTab === 2 && (
        <Box>
          {reports.length === 0 && !reportLoading && (
            <Alert severity="info" sx={{borderRadius: '12px'}}>
              No eval reports yet. Add test cases from the Source Library drawer, then run an eval.
            </Alert>
          )}

          {reports.length > 0 && (
            <Box sx={{display: 'flex', gap: 2}}>
              {/* Left pane: report list (multi-select) */}
              <Box sx={{width: 280, flexShrink: 0}}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                  <Typography sx={{fontSize: 13, fontWeight: 600, color: '#111827'}}>
                    Reports ({reports.length})
                    {selectedReportIds.length === 2 && (
                      <Typography component="span" sx={{fontSize: 11, color: '#3B82F6', ml: 0.5}}>
                        comparing
                      </Typography>
                    )}
                  </Typography>
                  <Box sx={{display: 'flex', gap: 0.25}}>
                    <Tooltip title={hideOrphanedReports ? 'Show all reports' : 'Hide reports with deleted prompts'}>
                      <IconButton size="small" onClick={() => setHideOrphanedReports(!hideOrphanedReports)} sx={{color: hideOrphanedReports ? '#3B82F6' : '#9CA3AF', '&:hover': {color: '#3B82F6'}, p: 0.25}}>
                        <Typography sx={{fontSize: 11, fontWeight: 600, lineHeight: 1}}>{hideOrphanedReports ? '👁' : '👁‍🗨'}</Typography>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Recalculate scores & backfill dates">
                      <IconButton size="small" onClick={handleRecalculate} disabled={recalculating} sx={{color: '#9CA3AF', '&:hover': {color: '#3B82F6'}, p: 0.25}}>
                        {recalculating ? <CircularProgress size={14} /> : <AutoFixHighIcon sx={{fontSize: 14}} />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Typography sx={{fontSize: 10, color: '#9CA3AF', mb: 0.5}}>
                  Click to select. {navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'}+click to compare two.
                </Typography>
                <Box sx={{bgcolor: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB', maxHeight: 'calc(100vh - 280px)', overflowY: 'auto'}}>
                  {reports.filter((r: any) => !hideOrphanedReports || r.promptExists !== false).map((r: any, i: number) => {
                    const score = r.weightedScore ?? (r.totalCases > 0 ? Math.round((r.passed / r.totalCases) * 100) : 0);
                    const isSelected = selectedReportIds.includes(r.id);
                    const selIndex = selectedReportIds.indexOf(r.id);
                    const shortModel = r.modelName ? r.modelName.split('/').pop() : null;
                    const isOrphaned = r.promptExists === false;
                    return (
                      <Box
                        key={r.id}
                        onClick={(e: React.MouseEvent) => {
                          if (e.metaKey || e.ctrlKey) {
                            // Toggle in multi-select (max 2)
                            {
                              const prev = selectedReportIds;
                              if (prev.includes(r.id)) setSelectedReportIds(prev.filter(id => id !== r.id));
                              else if (prev.length >= 2) setSelectedReportIds([prev[1], r.id]);
                              else setSelectedReportIds([...prev, r.id]);
                            }
                          } else {
                            // Single select (replaces)
                            setSelectedReportIds(isSelected && selectedReportIds.length === 1 ? [] : [r.id]);
                          }
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 1.5,
                          py: 1,
                          cursor: 'pointer',
                          bgcolor: isSelected ? '#F0F5FF' : 'transparent',
                          borderLeft: isSelected ? '3px solid #3B82F6' : '3px solid transparent',
                          borderBottom: i < reports.length - 1 ? '1px solid #F3F4F6' : 'none',
                          '&:hover': {bgcolor: isSelected ? '#F0F5FF' : '#F9FAFB'},
                        }}
                      >
                        {selectedReportIds.length === 2 && isSelected && (
                          <Typography sx={{fontSize: 10, fontWeight: 700, color: '#3B82F6', width: 14, textAlign: 'center', flexShrink: 0}}>
                            {selIndex === 0 ? 'A' : 'B'}
                          </Typography>
                        )}
                        <Box sx={{flex: 1, minWidth: 0}}>
                          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                            <Typography sx={{fontSize: 13, fontWeight: 500, color: isOrphaned ? '#9CA3AF' : '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...(isOrphaned && {textDecoration: 'line-through'})}}>
                              {r.promptName}
                            </Typography>
                            {isOrphaned && (
                              <Tooltip title="Prompt deleted — cannot rerun"><Chip label="no prompt" size="small" sx={{height: 14, fontSize: 8, fontWeight: 600, bgcolor: '#FEE2E2', color: '#991B1B'}} /></Tooltip>
                            )}
                            {shortModel && (
                              <Chip label={shortModel} size="small" sx={{height: 16, fontSize: 9, fontWeight: 500, bgcolor: '#EFF6FF', color: '#1E40AF'}} />
                            )}
                            <Chip
                              label={`${score}%`}
                              size="small"
                              sx={{
                                height: 18, fontSize: 10, fontWeight: 700,
                                bgcolor: score >= 80 ? '#D1FAE5' : score >= 50 ? '#FEF3C7' : '#FEE2E2',
                                color: score >= 80 ? '#065F46' : score >= 50 ? '#92400E' : '#991B1B',
                              }}
                            />
                          </Box>
                          <Typography sx={{fontSize: 10, color: '#9CA3AF'}}>
                            {r.passed}/{r.totalCases} passed · {new Date(r.ranAt).toLocaleDateString()}
                          </Typography>
                          {r.totalCost != null && r.totalCost > 0 && (
                            <Typography sx={{fontSize: 10, color: '#9CA3AF'}}>${r.totalCost.toFixed(4)}</Typography>
                          )}
                        </Box>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleDeleteReport(r.id); }}
                            sx={{color: '#D1D5DB', '&:hover': {color: '#EF4444'}, p: 0.25}}
                          >
                            <DeleteIcon sx={{fontSize: 14}} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* Right pane: single report or compare */}
              <Box sx={{flex: 1, minWidth: 0}}>
                {/* Single report view */}
                {selectedReportIds.length === 1 && (
                  <>
                    {reportLoading && (
                      <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                        <CircularProgress size={28} />
                      </Box>
                    )}
                    {report && (
                      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                        {report.promptExists === false && (
                          <Alert severity="warning" sx={{mb: 1.5, py: 0.25, fontSize: 12}}>
                            The prompt used for this report has been deleted. Reruns are disabled.
                          </Alert>
                        )}
                        <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                          {report.promptName !== 'current' && report.promptExists !== false && (
                            <Button
                              size="small"
                              startIcon={<EditIcon sx={{fontSize: 14}} />}
                              onClick={() => {
                                const match = prompts.find((p: any) => p.name === report.promptName);
                                if (match) {
                                  setEditingPromptName(match.name);
                                  setEditorContent(match.prompt);
                                  setIsCreating(false);
                                }
                                setActiveTab(1);
                              }}
                              sx={{textTransform: 'none', fontSize: 12, whiteSpace: 'nowrap'}}
                            >
                              View Prompt
                            </Button>
                          )}
                          {report.promptExists !== false && (
                            <Button
                              size="small"
                              startIcon={<PlayArrowIcon sx={{fontSize: 14}} />}
                              onClick={async () => {
                                try {
                                  const token = await (await import('../config/firebase')).auth.currentUser?.getIdToken();
                                  const result = await rerunReportMutation({
                                    variables: {reportId: report.id},
                                    context: {headers: {authorization: `Bearer ${token}`}},
                                  });
                                  const job = result.data?.rerunEvalReport;
                                  if (job) {
                                    setEvalJobId(job.id);
                                    setEvalJobType('EVAL_RERUN_REPORT');
                                    showToast({message: 'Rerunning all cases...', severity: 'info', action: {label: 'View job', to: `/org/jobs/${job.id}`}});
                                  }
                                } catch (err: any) {
                                  showToast({message: err.message || 'Rerun failed', severity: 'error'});
                                }
                              }}
                              sx={{textTransform: 'none', fontSize: 12, whiteSpace: 'nowrap'}}
                            >
                              Rerun all
                            </Button>
                          )}
                        </Box>
                        <EvalSummaryBar
                          totalCases={report.totalCases}
                          passed={report.passed}
                          failed={report.failed}
                          fieldAccuracy={report.fieldAccuracy}
                          guideableScore={report.guideableScore}
                          overallScore={report.overallScore}
                          weightedScore={report.weightedScore}
                          promptName={report.promptName}
                          modelName={report.modelName}
                          inputTokens={report.inputTokens}
                          outputTokens={report.outputTokens}
                          totalCost={report.totalCost}
                          baseline={baselineReport ? {
                            passed: baselineReport.passed,
                            totalCases: baselineReport.totalCases,
                            fieldAccuracy: baselineReport.fieldAccuracy,
                          } : undefined}
                        />
                        <EvalResultsTable
                          results={report.results}
                          testCases={testCases}
                          onRerunCase={report.promptExists !== false ? async (testCaseId) => {
                            try {
                              await rerunCaseMutation({
                                variables: {reportId: report.id, testCaseId},
                                refetchQueries: [{query: GET_EVAL_REPORT, variables: {id: report.id}}, {query: GET_EVAL_REPORTS}],
                              });
                              showToast({message: 'Case rerun complete', severity: 'success'});
                            } catch (err: any) {
                              showToast({message: err.message || 'Rerun failed', severity: 'error'});
                            }
                          } : undefined}
                        />
                      </Box>
                    )}
                  </>
                )}

                {/* Compare view (2 selected) */}
                {selectedReportIds.length === 2 && reportAData && reportBData && (
                  <EvalCompareView reportA={reportAData} reportB={reportBData} />
                )}

                {selectedReportIds.length === 0 && (
                  <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: '#9CA3AF'}}>
                    <Typography sx={{fontSize: 13}}>Select a report to view results, or {navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'}+click two to compare</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Tab: Test Cases */}
      {activeTab === 3 && (
        <Box>
          {testCases.length === 0 ? (
            <Alert severity="info" sx={{borderRadius: '12px'}}>
              No test cases yet. Open a source item in the Source Library, click the overflow menu, and select "Flag as eval case".
            </Alert>
          ) : (
            <Box sx={{bgcolor: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB'}}>
              {testCases.map((tc: any, i: number) => (
                <Box
                  key={tc.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 2,
                    py: 1.5,
                    borderBottom: i < testCases.length - 1 ? '1px solid #F3F4F6' : 'none',
                  }}
                >
                  <Box sx={{flex: 1, minWidth: 0}}>
                    <Typography sx={{fontSize: 13, fontWeight: 500, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                      {tc.sourceItemTitle}
                    </Typography>
                    <Typography sx={{fontSize: 11, color: '#9CA3AF'}}>
                      {tc.expected.guideable ? 'Guideable' : 'Not guideable'} · {tc.expected.topic} · {tc.expected.audience}
                    </Typography>
                  </Box>
                  <Typography sx={{fontSize: 11, color: '#9CA3AF'}}>{tc.itemType}</Typography>
                  <Tooltip title="Delete test case">
                    <IconButton size="small" onClick={() => handleDeleteCase(tc.id)} sx={{color: '#9CA3AF', '&:hover': {color: '#EF4444'}}}>
                      <DeleteIcon sx={{fontSize: 16}} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Tab: Multi-Model (hidden — subsumed by grid, kept for backwards compat) */}
      {activeTab === 99 && (
        <Box>
          {/* Model selection */}
          <Box sx={{bgcolor: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB', p: 2, mb: 2}}>
            <Typography sx={{fontSize: 14, fontWeight: 600, color: '#111827', mb: 1}}>Select Models (via OpenRouter)</Typography>
            <Typography sx={{fontSize: 12, color: '#6B7280', mb: 1.5}}>
              Run the same prompt against multiple models to compare accuracy and cost. Use any OpenRouter model ID.
            </Typography>

            {/* Suggested models */}
            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5}}>
              {SUGGESTED_MODELS.map(m => {
                const price = orPricing[m];
                const label = price
                  ? `${m.split('/').pop()} ($${formatPricePer1M(price.inputPer1M)}/$${formatPricePer1M(price.outputPer1M)})`
                  : m.split('/').pop();
                return (
                  <Chip
                    key={m}
                    label={label}
                    size="small"
                    variant={multiModelSelected.has(m) ? 'filled' : 'outlined'}
                    color={multiModelSelected.has(m) ? 'primary' : 'default'}
                    onClick={() => toggleMultiModel(m)}
                    sx={{fontSize: 11, cursor: 'pointer'}}
                  />
                );
              })}
            </Box>

            {/* Custom model input */}
            <Box sx={{display: 'flex', gap: 1, mb: 1.5}}>
              <TextField
                size="small"
                placeholder="provider/model-name (e.g. cohere/command-r)"
                value={multiModelCustom}
                onChange={e => setMultiModelCustom(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addCustomModel(); }}
                sx={{flex: 1, '& input': {fontSize: 12}}}
              />
              <Button size="small" variant="outlined" onClick={addCustomModel} disabled={!multiModelCustom.trim()} sx={{textTransform: 'none', fontSize: 12}}>
                Add
              </Button>
            </Box>

            {/* Selected models with pricing */}
            {multiModelSelected.size > 0 && (
              <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5}}>
                {Array.from(multiModelSelected).map(m => {
                  const price = orPricing[m];
                  const label = price
                    ? `${m} — $${formatPricePer1M(price.inputPer1M)}/$${formatPricePer1M(price.outputPer1M)} per 1M`
                    : m;
                  return (
                  <Chip
                    key={m}
                    label={label}
                    size="small"
                    onDelete={() => toggleMultiModel(m)}
                    sx={{fontSize: 11}}
                  />
                  );
                })}
              </Box>
            )}

            {/* Prompt selector + Run button */}
            <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
              <FormControl size="small" sx={{minWidth: 280}}>
                <Select
                  value={multiModelPrompt}
                  onChange={e => setMultiModelPrompt(e.target.value as string)}
                  displayEmpty
                  renderValue={(v) => {
                    if (!v) return <Typography sx={{fontSize: 12, color: '#6B7280'}}>current (built-in)</Typography>;
                    const p = prompts.find((pp: any) => pp.name === v);
                    return <Typography sx={{fontSize: 12}}>{p?.displayName || p?.name || v}{p ? formatScore(p) : ''}</Typography>;
                  }}
                  sx={{fontSize: 12, '& .MuiSelect-select': {py: 0.75}}}
                  MenuProps={{PaperProps: {sx: {maxHeight: 320}}}}
                >
                  <MenuItem value="">
                    <Box>
                      <Typography sx={{fontSize: 13, fontWeight: 500}}>current</Typography>
                      <Typography sx={{fontSize: 10, color: '#9CA3AF'}}>built-in prompt</Typography>
                    </Box>
                  </MenuItem>
                  {[...savedPrompts].sort((a: any, b: any) => (b.createdAt || '').localeCompare(a.createdAt || '')).map((p: any) => {
                    const score = p.lastEvalScore?.weightedScore ?? p.lastEvalScore?.guideableAccuracy ?? p.lastEvalScore?.accuracy;
                    return (
                      <MenuItem key={p.name} value={p.name}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, width: '100%'}}>
                          <Box sx={{flex: 1, minWidth: 0}}>
                            <Typography sx={{fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                              {p.displayName || p.name}
                            </Typography>
                            <Typography sx={{fontSize: 10, color: '#9CA3AF'}}>
                              {p.createdAt ? new Date(p.createdAt).toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'}) : 'no date'}
                              {p.origin ? ` · ${p.origin}` : ''}
                            </Typography>
                          </Box>
                          {score != null && (
                            <Chip
                              label={`${score}%`}
                              size="small"
                              sx={{
                                height: 20, fontSize: 10, fontWeight: 700,
                                bgcolor: score >= 80 ? '#D1FAE5' : score >= 50 ? '#FEF3C7' : '#FEE2E2',
                                color: score >= 80 ? '#065F46' : score >= 50 ? '#92400E' : '#991B1B',
                              }}
                            />
                          )}
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                size="small"
                startIcon={evalJobActive && evalJobType === 'EVAL_MULTI_MODEL' ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                onClick={handleRunMultiModelEval}
                disabled={evalJobActive || testCases.length === 0 || multiModelSelected.size === 0}
                sx={{textTransform: 'none', fontSize: 13}}
              >
                {evalJobActive && evalJobType === 'EVAL_MULTI_MODEL' ? 'Running...' : `Run ${multiModelSelected.size} Models`}
              </Button>
            </Box>
          </Box>

          {/* Results table */}
          {multiModelReports.length > 0 && (() => {
            // Unique prompts used across multi-model runs
            const mmPromptNames: string[] = Array.from(new Set<string>(multiModelReports.map((r: any) => r.promptName as string))).sort();

            // Filter by prompt
            const filtered = mmFilterPrompt
              ? multiModelReports.filter((r: any) => r.promptName === mmFilterPrompt)
              : multiModelReports;

            // Compute derived values for sorting
            const withDerived = filtered.map((r: any) => {
              const score = r.weightedScore ?? (r.totalCases > 0 ? Math.round((r.passed / r.totalCases) * 100) : 0);
              const tokens = (r.inputTokens || 0) + (r.outputTokens || 0);
              const cost = r.totalCost || 0;
              return {...r, _score: score, _tokens: tokens, _cost: cost};
            });

            // Precompute value scores for the filtered set
            const rawValues = withDerived.map((r: any) => {
              if (r._score < 75 || r._cost <= 0) return null;
              return ((r._score - 75) / 25) / r._cost;
            });
            const maxValue = Math.max(...rawValues.filter((v: number | null): v is number => v != null), 0);
            const valueMap: Record<string, number | null> = {};
            withDerived.forEach((r: any, i: number) => {
              const raw = rawValues[i];
              valueMap[r.id] = raw != null && maxValue > 0 ? Math.round((raw / maxValue) * 100) : null;
            });

            // Sort
            const sortKey = mmSortCol;
            const asc = mmSortAsc;
            const sorted = [...withDerived].sort((a: any, b: any) => {
              let av: number, bv: number;
              switch (sortKey) {
                case 'model': return asc ? (a.modelName || '').localeCompare(b.modelName || '') : (b.modelName || '').localeCompare(a.modelName || '');
                case 'passed': av = a.passed; bv = b.passed; break;
                case 'failed': av = a.failed; bv = b.failed; break;
                case 'score': av = a._score; bv = b._score; break;
                case 'value': av = valueMap[a.id] ?? -1; bv = valueMap[b.id] ?? -1; break;
                case 'tokens': av = a._tokens; bv = b._tokens; break;
                case 'cost': av = a._cost; bv = b._cost; break;
                case 'ranAt': return asc ? a.ranAt.localeCompare(b.ranAt) : b.ranAt.localeCompare(a.ranAt);
                default: av = a._score; bv = b._score;
              }
              return asc ? av - bv : bv - av;
            });

            const handleSort = (col: string) => {
              if (mmSortCol === col) { setMmSortAsc(!mmSortAsc); }
              else { setMmSortCol(col); setMmSortAsc(false); }
            };

            const sortArrow = (col: string) => mmSortCol === col ? (mmSortAsc ? ' \u25B2' : ' \u25BC') : '';

            // Prompt info for display
            const promptForDisplay = mmFilterPrompt || (mmPromptNames.length === 1 ? mmPromptNames[0] : null);
            const promptScore = promptForDisplay
              ? prompts.find((p: any) => p.name === promptForDisplay)?.lastEvalScore
              : null;

            const gridCols = '1fr 70px 70px 80px 80px 100px 80px 140px 36px';

            return (
            <Box sx={{bgcolor: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'hidden'}}>
              {/* Toolbar: prompt info + filter */}
              <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderBottom: '1px solid #E5E7EB', bgcolor: '#FAFAFA'}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                  <Typography sx={{fontSize: 12, color: '#6B7280'}}>Prompt:</Typography>
                  <Typography sx={{fontSize: 12, fontWeight: 600, color: '#111827'}}>
                    {promptForDisplay || 'mixed'}
                  </Typography>
                  {promptScore && (() => {
                    const acc = Number(promptScore.weightedScore ?? promptScore.guideableAccuracy ?? promptScore.accuracy ?? 0);
                    return (
                      <Chip
                        label={`${acc}%`}
                        size="small"
                        sx={{height: 20, fontSize: 11, fontWeight: 600, bgcolor: acc >= 80 ? '#D1FAE5' : '#FEF3C7', color: acc >= 80 ? '#065F46' : '#92400E'}}
                      />
                    );
                  })()}
                </Box>
                {mmPromptNames.length > 1 && (
                  <FormControl size="small" sx={{minWidth: 140}}>
                    <Select
                      native
                      value={mmFilterPrompt}
                      onChange={(e: any) => setMmFilterPrompt(e.target.value as string)}
                      sx={{fontSize: 11, height: 28}}
                    >
                      <option value="">All prompts</option>
                      {mmPromptNames.map((pn: string) => (
                        <option key={pn} value={pn}>{pn}</option>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
              {/* Header */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: gridCols,
                  px: 2,
                  py: 1,
                  borderBottom: '1px solid #E5E7EB',
                  bgcolor: '#FAFAFA',
                }}
              >
                {[
                  {label: 'Model', key: 'model'},
                  {label: 'Passed', key: 'passed'},
                  {label: 'Failed', key: 'failed'},
                  {label: 'Score', key: 'score'},
                  {label: 'Value', key: 'value'},
                  {label: 'Tokens', key: 'tokens'},
                  {label: 'Cost', key: 'cost'},
                  {label: 'Ran At', key: 'ranAt'},
                  {label: '', key: ''},
                ].map(h => (
                  <Typography
                    key={h.key}
                    onClick={() => handleSort(h.key)}
                    sx={{fontSize: 11, fontWeight: 600, color: mmSortCol === h.key ? '#111827' : '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', cursor: 'pointer', userSelect: 'none', '&:hover': {color: '#111827'}}}
                  >
                    {h.label}{sortArrow(h.key)}
                  </Typography>
                ))}
              </Box>
              {/* Rows */}
              {sorted.map((r: any, i: number) => {
                const score = Math.round(r._score);
                const value = valueMap[r.id] ?? null;
                return (
                  <Box
                    key={r.id}
                    onClick={() => { setSelectedReportIds([r.id]); setActiveTab(2); }}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: gridCols,
                      px: 2,
                      py: 1.5,
                      borderBottom: '1px solid #F3F4F6',
                      cursor: 'pointer',
                      '&:hover': {bgcolor: '#F9FAFB'},
                    }}
                  >
                    <Typography sx={{fontSize: 13, fontWeight: 500, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                      {r.modelName}
                    </Typography>
                    <Typography sx={{fontSize: 13, color: '#059669', fontWeight: 600}}>{r.passed}</Typography>
                    <Typography sx={{fontSize: 13, color: r.failed > 0 ? '#DC2626' : '#6B7280'}}>{r.failed}</Typography>
                    <Typography sx={{fontSize: 13, fontWeight: 600, color: score >= 80 ? '#059669' : score >= 50 ? '#D97706' : '#DC2626'}}>
                      {score}%
                    </Typography>
                    <Typography sx={{fontSize: 13, fontWeight: 600, color: value != null ? (value >= 80 ? '#059669' : value >= 40 ? '#D97706' : '#6B7280') : '#D1D5DB'}}>
                      {value != null ? value : '—'}
                    </Typography>
                    <Typography sx={{fontSize: 12, color: '#6B7280'}}>
                      {r._tokens > 0 ? r._tokens.toLocaleString() : '-'}
                    </Typography>
                    <Typography sx={{fontSize: 12, color: '#6B7280', fontWeight: r.totalCost ? 500 : 400}}>
                      {r.totalCost != null ? `$${r.totalCost.toFixed(4)}` : '-'}
                    </Typography>
                    <Typography sx={{fontSize: 12, color: '#9CA3AF'}}>
                      {new Date(r.ranAt).toLocaleString()}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleDeleteReport(r.id); }}
                      sx={{color: '#D1D5DB', '&:hover': {color: '#EF4444'}, p: 0.25}}
                    >
                      <DeleteIcon sx={{fontSize: 14}} />
                    </IconButton>
                  </Box>
                );
              })}
              {/* Totals row */}
              {(() => {
                const sumTokens = sorted.reduce((s: number, r: any) => s + r._tokens, 0);
                const sumCost = sorted.reduce((s: number, r: any) => s + r._cost, 0);
                return (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: gridCols,
                      px: 2,
                      py: 1,
                      bgcolor: '#FAFAFA',
                      borderTop: '1px solid #E5E7EB',
                    }}
                  >
                    <Typography sx={{fontSize: 12, fontWeight: 700, color: '#374151'}}>Total ({sorted.length})</Typography>
                    <Typography sx={{fontSize: 12}} />
                    <Typography sx={{fontSize: 12}} />
                    <Typography sx={{fontSize: 12}} />
                    <Typography sx={{fontSize: 12}} />
                    <Typography sx={{fontSize: 12, fontWeight: 600, color: '#374151'}}>
                      {sumTokens > 0 ? sumTokens.toLocaleString() : '-'}
                    </Typography>
                    <Typography sx={{fontSize: 12, fontWeight: 700, color: '#374151'}}>
                      {sumCost > 0 ? `$${sumCost.toFixed(4)}` : '-'}
                    </Typography>
                    <Typography sx={{fontSize: 12}} />
                    <Typography sx={{fontSize: 12}} />
                  </Box>
                );
              })()}
            </Box>
            );
          })()}

          {multiModelReports.length === 0 && !evalJobActive && (
            <Alert severity="info" sx={{borderRadius: '12px'}}>
              No multi-model results yet. Select models above and click Run to compare.
            </Alert>
          )}
        </Box>
      )}

      {/* Tab: Prompts */}
      {activeTab === 1 && (
        <Box sx={{display: 'flex', gap: 2}}>
          {/* Prompt list */}
          <Box sx={{width: 260, flexShrink: 0}}>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
              <Typography sx={{fontSize: 13, fontWeight: 600, color: '#111827'}}>Prompts</Typography>
              <Box sx={{display: 'flex', gap: 0.25}}>
                <Tooltip title="Re-eval all prompts with a model">
                  <IconButton size="small" onClick={() => setReEvalAllOpen(true)} disabled={evalJobActive || testCases.length === 0} sx={{color: '#6B7280'}}>
                    <PlayArrowIcon sx={{fontSize: 16}} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="New prompt">
                  <IconButton size="small" onClick={handleNewPrompt} sx={{color: '#6B7280'}}>
                    <AddIcon sx={{fontSize: 16}} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box sx={{bgcolor: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB'}}>
              {prompts.map((p: any, i: number) => {
                const label = p.displayName || p.name;
                const subtitle = p.displayName && p.displayName !== p.name ? p.name : null;
                return (
                <Box
                  key={p.name}
                  onClick={() => handleEditPrompt(p)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    cursor: 'pointer',
                    bgcolor: editingPromptName === p.name ? '#F0F5FF' : 'transparent',
                    borderBottom: i < prompts.length - 1 ? '1px solid #F3F4F6' : 'none',
                    '&:hover': {bgcolor: editingPromptName === p.name ? '#F0F5FF' : '#F9FAFB'},
                  }}
                >
                  <Box sx={{flex: 1, minWidth: 0}}>
                    {renamingPrompt === p.name ? (
                      <TextField
                        size="small"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onBlur={() => handleRenamePrompt(p.name)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleRenamePrompt(p.name); if (e.key === 'Escape') setRenamingPrompt(null); }}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                        sx={{width: '100%', '& input': {fontSize: 12, py: 0.5}}}
                      />
                    ) : (
                      <>
                        <Typography sx={{fontSize: 13, fontWeight: 500, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                          {label}
                        </Typography>
                        {p.name === 'current' && (
                          <Typography sx={{fontSize: 10, color: '#9CA3AF'}}>built-in (read-only)</Typography>
                        )}
                        {subtitle && (
                          <Typography sx={{fontSize: 10, color: '#9CA3AF'}}>{subtitle}</Typography>
                        )}
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap'}}>
                          {p.createdAt && (
                            <Typography sx={{fontSize: 10, color: '#9CA3AF'}}>{new Date(p.createdAt).toLocaleDateString()}</Typography>
                          )}
                          {p.lastEvalScore && (
                            <Typography
                              component="span"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                setSelectedReportIds([p.lastEvalScore.reportId]);
                                setActiveTab(2);
                              }}
                              sx={{
                                fontSize: 10,
                                color: (p.lastEvalScore.weightedScore ?? p.lastEvalScore.guideableAccuracy ?? p.lastEvalScore.accuracy) >= 80 ? '#059669' : (p.lastEvalScore.weightedScore ?? p.lastEvalScore.guideableAccuracy ?? p.lastEvalScore.accuracy) >= 50 ? '#D97706' : '#DC2626',
                                fontWeight: 600,
                                cursor: 'pointer',
                                '&:hover': {textDecoration: 'underline'},
                              }}
                            >
                              {p.lastEvalScore.weightedScore ?? p.lastEvalScore.guideableAccuracy ?? p.lastEvalScore.accuracy}%
                            </Typography>
                          )}
                          {p.origin && (
                            <Typography sx={{fontSize: 9, color: '#9CA3AF', bgcolor: '#F3F4F6', px: 0.5, borderRadius: '3px'}}>{p.origin}</Typography>
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                  {p.name !== 'current' && renamingPrompt !== p.name && (
                    <>
                      <Tooltip title="Rename">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setRenamingPrompt(p.name); setRenameValue(p.name); }}
                          sx={{color: '#9CA3AF', '&:hover': {color: '#3B82F6'}}}
                        >
                          <EditIcon sx={{fontSize: 14}} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleDeletePrompt(p.name); }}
                          sx={{color: '#9CA3AF', '&:hover': {color: '#EF4444'}}}
                        >
                          <DeleteIcon sx={{fontSize: 14}} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
                );
              })}
              {prompts.length === 0 && (
                <Box sx={{p: 2}}>
                  <Typography sx={{fontSize: 12, color: '#9CA3AF'}}>Loading...</Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Editor */}
          <Box sx={{flex: 1, minWidth: 0}}>
            {(editingPromptName || isCreating) ? (
              <Box>
                {/* Editor header */}
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1.5}}>
                  {isCreating ? (
                    <TextField
                      size="small"
                      placeholder="Prompt name (e.g. v2, experiment-1)"
                      value={newPromptName}
                      onChange={(e) => setNewPromptName(e.target.value)}
                      sx={{flex: 1, '& input': {fontSize: 13}}}
                    />
                  ) : (() => {
                    const editingP = prompts.find((p: any) => p.name === editingPromptName);
                    const editLabel = editingP?.displayName || editingPromptName;
                    return (
                    <Typography sx={{fontSize: 15, fontWeight: 600, color: '#111827', flex: 1}}>
                      {editLabel}
                      {editingP?.displayName && editingP.displayName !== editingPromptName && (
                        <Typography component="span" sx={{fontSize: 11, color: '#9CA3AF', ml: 1}}>({editingPromptName})</Typography>
                      )}
                      {editingPromptName === 'current' && <Typography component="span" sx={{fontSize: 12, color: '#9CA3AF', ml: 1}}>read-only</Typography>}
                    </Typography>
                    );
                  })()}
                  {editingPromptName !== 'current' && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<SaveIcon sx={{fontSize: 14}} />}
                      onClick={handleSavePrompt}
                      disabled={isCreating && !newPromptName.trim()}
                      sx={{textTransform: 'none', fontSize: 12}}
                    >
                      Save
                    </Button>
                  )}
                  {editingPromptName !== 'current' && (
                    <Tooltip title="Run eval with this prompt">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={evalJobActive && evalJobType === 'EVAL_RUN' ? <CircularProgress size={12} /> : <PlayArrowIcon sx={{fontSize: 14}} />}
                        onClick={() => { setRunDialogPrompt(isCreating ? newPromptName.trim() : editingPromptName || 'current'); setRunDialogOpen(true); }}
                        disabled={evalJobActive || testCases.length === 0 || (isCreating && !newPromptName.trim())}
                        sx={{textTransform: 'none', fontSize: 12}}
                      >
                        Run
                      </Button>
                    </Tooltip>
                  )}
                  {editingPromptName !== 'current' && !isCreating && (
                    <>
                      <Box sx={{borderLeft: '1px solid #E5E7EB', mx: 0.25, height: 24}} />
                      <Tooltip title="Generate improved prompts with multiple LLMs, evaluate them, and iterate">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={evalJobActive && evalJobType === 'EVAL_ITERATE' ? <CircularProgress size={12} /> : <AutoFixHighIcon sx={{fontSize: 14}} />}
                          onClick={() => setIterateOpen(true)}
                          disabled={evalJobActive || testCases.length === 0}
                          sx={{textTransform: 'none', fontSize: 12, whiteSpace: 'nowrap'}}
                        >
                          {evalJobActive && evalJobType === 'EVAL_ITERATE' ? 'Iterating...' : 'Iterate'}
                        </Button>
                      </Tooltip>
                    </>
                  )}
                </Box>

                {/* Prompt text editor */}
                <TextField
                  multiline
                  minRows={12}
                  maxRows={30}
                  fullWidth
                  value={editorContent}
                  onChange={(e) => setEditorContent(e.target.value)}
                  disabled={editingPromptName === 'current'}
                  sx={{
                    mb: 2,
                    '& .MuiInputBase-root': {fontSize: 12, fontFamily: 'monospace', lineHeight: 1.6},
                    '& .MuiOutlinedInput-root': {borderRadius: '8px'},
                  }}
                />

              </Box>
            ) : (
              <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: '#9CA3AF'}}>
                <Typography sx={{fontSize: 13}}>Select a prompt to edit, or create a new one</Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Iterate dialog */}
      {/* Re-eval All Prompts Dialog */}
      <Dialog open={reEvalAllOpen} onClose={() => setReEvalAllOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{fontSize: 16, fontWeight: 600, pb: 1}}>Re-eval All Prompts</DialogTitle>
        <DialogContent>
          <Typography sx={{fontSize: 12, color: '#6B7280', mb: 2}}>
            Run eval for every prompt ({prompts.length} total) using the selected model. Creates one background job per prompt.
          </Typography>
          <Typography sx={{fontSize: 13, fontWeight: 600, color: '#111827', mb: 0.5}}>Model</Typography>
          <FormControl size="small" fullWidth>
            <Select
              value={evalModel}
              onChange={(e) => setEvalModel(e.target.value as string)}
              renderValue={(v) => {
                const p = orPricing[v as string];
                return <Typography sx={{fontSize: 13}}>{p?.name || (v as string).split('/').pop()}</Typography>;
              }}
              sx={{fontSize: 13}}
              MenuProps={{PaperProps: {sx: {maxHeight: 300}}}}
            >
              {SUGGESTED_MODELS.map(m => {
                const p = orPricing[m];
                return (
                  <MenuItem key={m} value={m} sx={{fontSize: 13}}>
                    {p?.name || m.split('/').pop()}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{px: 3, pb: 2}}>
          <Button onClick={() => setReEvalAllOpen(false)} size="small" sx={{textTransform: 'none', color: '#6B7280'}}>Cancel</Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleReEvalAllPrompts}
            disabled={testCases.length === 0}
            sx={{textTransform: 'none'}}
          >
            Run all ({prompts.length} prompts)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Run Eval Dialog */}
      <Dialog open={runDialogOpen} onClose={() => setRunDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{fontSize: 16, fontWeight: 600, pb: 1}}>Run Eval</DialogTitle>
        <DialogContent>
          <Typography sx={{fontSize: 12, color: '#6B7280', mb: 2}}>
            Run the selected prompt against all test cases. Select one or more models.
          </Typography>

          <Typography sx={{fontSize: 13, fontWeight: 600, color: '#111827', mb: 0.5}}>Prompt</Typography>
          <FormControl size="small" fullWidth sx={{mb: 2}}>
            <Select
              value={runDialogPrompt}
              onChange={(e) => setRunDialogPrompt(e.target.value as string)}
              sx={{fontSize: 13}}
            >
              <MenuItem value="current" sx={{fontSize: 13}}>Current (built-in)</MenuItem>
              {savedPrompts.map((p: any) => (
                <MenuItem key={p.name} value={p.name} sx={{fontSize: 13}}>
                  {p.displayName || p.name}{p.lastEvalScore ? ` (${p.lastEvalScore.weightedScore ?? p.lastEvalScore.accuracy}%)` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography sx={{fontSize: 13, fontWeight: 600, color: '#111827', mb: 0.75}}>
            Models
            <Typography component="span" sx={{fontSize: 11, color: '#9CA3AF', ml: 1}}>
              ({runDialogModels.size} selected)
            </Typography>
          </Typography>
          <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1}}>
            {SUGGESTED_MODELS.map(m => {
              const selected = runDialogModels.has(m);
              const p = orPricing[m];
              const name = p?.name || m.split('/').pop() || m;
              const price = p ? `$${formatPricePer1M(p.inputPer1M)}/$${formatPricePer1M(p.outputPer1M)} per M` : '';
              return (
                <Chip
                  key={m}
                  label={price ? `${name} · ${price}` : name}
                  size="small"
                  variant={selected ? 'filled' : 'outlined'}
                  color={selected ? 'primary' : 'default'}
                  onClick={() => {
                    const next = new Set(runDialogModels);
                    if (selected) next.delete(m); else next.add(m);
                    setRunDialogModels(next);
                  }}
                  sx={{fontSize: 11, fontWeight: selected ? 600 : 400, cursor: 'pointer'}}
                />
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{px: 3, pb: 2}}>
          <Button onClick={() => setRunDialogOpen(false)} size="small" sx={{textTransform: 'none', color: '#6B7280'}}>Cancel</Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              const models = Array.from(runDialogModels);
              if (models.length === 0) {
                showToast({message: 'Select at least one model', severity: 'error'});
                return;
              }
              if (models.length === 1) {
                handleRunEval(runDialogPrompt, models[0]);
              } else {
                // Multi-model: use the dedicated mutation
                setRunDialogOpen(false);
                const promptName = runDialogPrompt === 'current' ? undefined : runDialogPrompt;
                runMultiModelEvalMutation({variables: {promptName, modelNames: models}}).then(result => {
                  const job = result.data?.runMultiModelEval;
                  if (job) {
                    setEvalJobId(job.id);
                    setEvalJobType('EVAL_MULTI_MODEL');
                    showToast({message: `Eval started across ${models.length} models...`, severity: 'info', action: {label: 'View job', to: `/org/jobs/${job.id}`}});
                  }
                }).catch((err: any) => {
                  showToast({message: `Eval failed: ${err.message}`, severity: 'error'});
                });
              }
            }}
            disabled={testCases.length === 0 || runDialogModels.size === 0}
            sx={{textTransform: 'none'}}
          >
            Run{runDialogModels.size > 1 ? ` (${runDialogModels.size} models)` : ''}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={iterateOpen} onClose={() => setIterateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{fontSize: 16, fontWeight: 600, pb: 1}}>Iterate on Prompt</DialogTitle>
        <DialogContent>
          <Typography sx={{fontSize: 12, color: '#6B7280', mb: 2, lineHeight: 1.5}}>
            Each iteration generates candidate prompts using the selected models, evaluates them all, and feeds the winner into the next round.
          </Typography>

          {/* Iterations */}
          <Box sx={{mb: 2}}>
            <Typography sx={{fontSize: 13, fontWeight: 600, color: '#111827', mb: 0.5}}>Iterations</Typography>
            <FormControl size="small" sx={{minWidth: 100}}>
              <Select
                value={iterateIterations}
                onChange={(e) => setIterateIterations(e.target.value as number)}
                sx={{fontSize: 13}}
              >
                {[1, 2, 3, 5, 10].map(n => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Eval model */}
          <Box sx={{mb: 2}}>
            <Typography sx={{fontSize: 13, fontWeight: 600, color: '#111827', mb: 0.5}}>Eval Model</Typography>
            <Typography sx={{fontSize: 11, color: '#9CA3AF', mb: 0.5}}>Used to run test cases against each candidate prompt</Typography>
            <FormControl size="small" sx={{minWidth: 240}}>
              <Select
                value={evalModel}
                onChange={(e) => setEvalModel(e.target.value as string)}
                renderValue={(v) => {
                  const p = orPricing[v as string];
                  return <Typography sx={{fontSize: 12}}>{p?.name || (v as string).split('/').pop()}</Typography>;
                }}
                sx={{fontSize: 12}}
                MenuProps={{PaperProps: {sx: {maxHeight: 300}}}}
              >
                {SUGGESTED_MODELS.map(m => {
                  const p = orPricing[m];
                  return (
                    <MenuItem key={m} value={m} sx={{py: 0.5, px: 1.5}}>
                      <Box sx={{display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', gap: 1}}>
                        <Typography sx={{fontSize: 12}}>{p?.name || m.split('/').pop()}</Typography>
                        {p && <Typography sx={{fontSize: 10, color: '#9CA3AF'}}>${formatPricePer1M(p.inputPer1M)}/${formatPricePer1M(p.outputPer1M)}</Typography>}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>

          {/* Generation models */}
          <Box>
            <Typography sx={{fontSize: 13, fontWeight: 600, color: '#111827', mb: 0.5}}>Generation Models</Typography>
            <Typography sx={{fontSize: 11, color: '#9CA3AF', mb: 1}}>Each model generates a candidate prompt per iteration — more models = more candidates to compete</Typography>
            {GENERATION_MODELS.map(m => {
              const p = orPricing[m];
              const checked = iterateGenerationModels.has(m);
              return (
                <Box
                  key={m}
                  onClick={() => setIterateGenerationModels(prev => {
                    const next = new Set(prev);
                    if (next.has(m)) next.delete(m); else next.add(m);
                    return next;
                  })}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25, cursor: 'pointer',
                    '&:hover': {bgcolor: '#F9FAFB'}, borderRadius: '4px', px: 0.5,
                  }}
                >
                  <Checkbox checked={checked} size="small" sx={{p: 0.25}} />
                  <Typography sx={{fontSize: 12, flex: 1}}>{p?.name || m.split('/').pop()}</Typography>
                  {p && <Typography sx={{fontSize: 10, color: '#9CA3AF'}}>${formatPricePer1M(p.inputPer1M)}/${formatPricePer1M(p.outputPer1M)}</Typography>}
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{px: 3, pb: 2}}>
          <Button onClick={() => setIterateOpen(false)} size="small" sx={{textTransform: 'none'}}>Cancel</Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => handleIterate(editingPromptName || undefined)}
            disabled={iterateGenerationModels.size === 0 || testCases.length === 0}
            startIcon={<AutoFixHighIcon sx={{fontSize: 14}} />}
            sx={{textTransform: 'none'}}
          >
            Start ({iterateIterations} iteration{iterateIterations > 1 ? 's' : ''}, {iterateGenerationModels.size} model{iterateGenerationModels.size > 1 ? 's' : ''})
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
